import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import avatar from '../images/avatar-illustration.jpg';
import { stampContainer, stampUp } from '../lib/motion';

const techStack = ['React', 'React Native', 'Node.js', 'Express', 'Java', 'Tailwind'];

const stats = [
  { value: '8,000+', label: 'App users' },
  { value: '750+', label: 'Rides completed' },
  { value: '2+ yrs', label: 'Building products' },
];

const Hero = () => {
  return (
    <section
      id="home"
      className="relative bg-paper dark:bg-noir brutal-grid-bg overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stampContainer}
          initial="hidden"
          animate="visible"
          className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center"
        >
          {/* Left: copy */}
          <div>
            <motion.p variants={stampUp} className="font-display text-lg font-bold text-ink dark:text-cream mb-4">
              Hi, I'm Tushar.
            </motion.p>

            <motion.h1
              variants={stampUp}
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.98] tracking-tight text-ink dark:text-cream mb-6"
            >
              I BUILD
              <br />
              PRODUCTS THAT{' '}
              <span className="inline-block -rotate-2 bg-sun px-3 brutal-border brutal-shadow-sm">
                SHIP.
              </span>
            </motion.h1>

            <motion.p variants={stampUp} className="text-lg text-ink/80 dark:text-cream/80 max-w-xl leading-relaxed mb-8">
              Co-Founder &amp; CTO of Totowala Technologies, a driver-first ride-booking platform.
              I design, build, and ship full-stack web and mobile products with React, Node.js,
              and React Native, from idea to production.
            </motion.p>

            <motion.div variants={stampUp} className="flex flex-wrap gap-4 mb-12">
              <a
                href="#projects"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-mint text-ink font-display font-bold brutal-border brutal-hover"
              >
                See my work
                <ArrowUpRight className="w-5 h-5" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-cream dark:bg-card text-ink dark:text-cream font-display font-bold brutal-border brutal-hover"
              >
                Let's talk
              </a>
            </motion.div>

            <motion.div variants={stampUp} className="flex flex-wrap gap-6 mb-8">
              {stats.map((stat) => (
                <div key={stat.label} className="min-w-[100px]">
                  <div className="font-display text-3xl font-extrabold text-ink dark:text-cream">{stat.value}</div>
                  <div className="text-sm font-semibold text-ink/60 dark:text-cream/60">{stat.label}</div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={stampUp} className="hidden sm:flex flex-wrap gap-2">
              {techStack.map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1.5 text-sm font-bold rounded-full bg-paper dark:bg-noir text-ink dark:text-cream brutal-border-thin"
                >
                  {tech}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right: portrait illustration, already a finished sticker-style
              composition, so it carries its own background/shapes instead
              of stacking another decorative backdrop behind it. */}
          <motion.div variants={stampUp} className="relative mx-auto w-full max-w-sm lg:max-w-md">
            <div className="relative rounded-[2rem] overflow-hidden brutal-border brutal-shadow-lg bg-[#f5f0e6] aspect-square">
              <img
                src={avatar}
                alt="Illustrated portrait of Tushar Anand"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="absolute -top-6 -right-4 sm:-right-8 rounded-full bg-sun px-5 py-3 brutal-border brutal-shadow-sm rotate-6">
              <span className="font-display font-extrabold text-ink">Tushar</span>
            </div>

            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:-right-2 flex items-center gap-2 rounded-full bg-mint px-4 py-2.5 brutal-border brutal-shadow-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ink/60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ink" />
              </span>
              <span className="text-sm font-bold text-ink whitespace-nowrap">Available for work</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
